import capitalize from 'lodash/capitalize';
import { ListingCategory, Transport } from '../../../graphql.generated';
import { ListingFragment } from './Listing.fragments.generated';

export type MappedListingFragment = Pick<
    ListingFragment,
    | 'listingId'
    | 'publicationStatus'
    | 'numberOfImages'
    | 'imagePreview'
    | 'title'
    | 'featurePreview'
    | 'publishedOn'
    | 'displayAddress'
    | 'statusSummary'
    | 'section'
    | 'tags'
    | 'listingUris'
> & {
    listingLinkHref: string;
    contactLink: string;
    nearestTransport: Transport | null;
    displayPrice?: string | null;
    agentLogoUrl?: string | null;
    agentName?: string | null;
    shortDisplayAddress: string;
    listingSection: ListingSection | null;
};

// known aberrations that we want to avoid putting on the card!
const INVALID_STREET_NAMES = [
    'No Street Name Specified',
    'none',
    'n/d',
    'Exact location on request',
    'bedroom apartment',
    'bedroom villa',
    'N/A',
    '.'
];

type ListingSection = 'for-sale' | 'to-rent' | 'overseas' | 'commercial';

const getListingSection = ({
    section,
    category
}: {
    section?: string | null;
    category?: ListingCategory | null;
}): ListingSection | null => {
    if (!section) {
        return null;
    }
    if (
        ['for-sale', 'new-homes'].includes(section) &&
        category === 'residential'
    ) {
        return 'for-sale';
    } else if (section === 'to-rent' && category === 'residential') {
        return 'to-rent';
    } else if (category === 'commercial') {
        return 'commercial';
    } else if (section === 'overseas') {
        return 'overseas';
    }
    return 'for-sale';
};

const getNearestTransport = (
    transports: Array<Transport | null | undefined> | null | undefined
): Transport | null => {
    if (!transports?.length) return null;

    const filteredTransports = transports.filter(
        (transport) => !!transport?.distanceInMiles
    ) as Array<Transport>;

    const sortedTransports = [...filteredTransports].sort(
        (a, b) => (a?.distanceInMiles ?? 0) - (b?.distanceInMiles ?? 0)
    );

    return sortedTransports[0] || null;
};

const getContactLink = (
    category?: ListingCategory | null,
    contact?: string | null,
    section?: string | null
): string => {
    if (!section || category !== ListingCategory.Residential) {
        return contact || '';
    }
    if (section && ['to-rent', 'for-sale', 'new-homes'].includes(section)) {
        return contact?.replace('contact', 'details/contact') || '';
    }
    return contact || '';
};

const getShortAddress = (
    streetName: string | undefined | null,
    postalCode: string | undefined | null
): string => {
    if (!streetName || !postalCode) {
        return '';
    }
    if (INVALID_STREET_NAMES.includes(streetName)) {
        return '';
    }
    const capitalizedStreetName = streetName
        .split(' ')
        .map(capitalize)
        .join(' ');

    return `${capitalizedStreetName}, ${postalCode}`;
};

export const mapListingFragment = (
    listing: ListingFragment
): MappedListingFragment => {
    const {
        listingId,
        publicationStatus,
        numberOfImages,
        imagePreview,
        title,
        featurePreview,
        publishedOn,
        displayAddress,
        statusSummary,
        section,
        tags,
        transports,
        category,
        listingUris
    } = listing;
    const listingLinkHref = listing.listingUris?.detail || '';
    const contactLink = getContactLink(
        category,
        listing.listingUris.contact,
        section
    );
    const nearestTransport = getNearestTransport(transports);
    const displayPrice = listing.pricing?.label;
    const agentLogoUrl = listing.branch?.logoUrl;
    const agentName = listing.branch?.name;
    const shortDisplayAddress = getShortAddress(
        listing.location?.streetName,
        listing.location?.postalCode
    );
    const listingSection = getListingSection({ section, category });
    return {
        listingId,
        publicationStatus,
        numberOfImages,
        imagePreview,
        title,
        featurePreview,
        publishedOn,
        displayAddress,
        listingUris,
        listingLinkHref,
        contactLink,
        nearestTransport,
        displayPrice,
        agentLogoUrl,
        agentName,
        shortDisplayAddress,
        statusSummary,
        section,
        tags,
        listingSection
    };
};
